import { useQuery } from '@apollo/client';
import type { PropsWithChildren } from 'react';

import { BottomBarEnabledModule } from '@xing-com/bottom-bar-enabled-module';
import { BrowserBarOpenModule } from '@xing-com/browser-bar-open-module';
import { ActivityCenterModule } from '@xing-com/crate-activity-center-module';
import { ReachLinkModule } from '@xing-com/crate-communication-reach-link-module';
import { useRuntime } from '@xing-com/crate-xinglet/internal';
import { FeatureSwitchesModule } from '@xing-com/feature-switches-module';
import type { LoginState } from '@xing-com/hub';
import { asyncResult } from '@xing-com/hub';
import { ViewerDataModule } from '@xing-com/initial-data-module';
import { NotificationsModule } from '@xing-com/notifications-module';
import { OmViewModule } from '@xing-com/om-view-module';
import { OmViewAnimation } from '@xing-com/platform-layout-om-view-animation';
import { SplitkitModule } from '@xing-com/splitkit';
import { ThemingModule } from '@xing-com/theming-module';

import { FeatureSwitchesQueryDocument } from './feature-switches.gql-types';
import { getConditionalRegisteredExperiments } from './registered-experiments';

type StaticHubProps = PropsWithChildren;

const StaticHub: React.FC<StaticHubProps> = ({ children }) => {
  const { config, metadataMap } = useRuntime();
  const { activeThemeCookie, viewerCountry } = config;
  const conditionalRegisteredExperiments = getConditionalRegisteredExperiments(
    metadataMap,
    viewerCountry
  );

  return (
    <ThemingModule activeThemeCookie={activeThemeCookie}>
      <ReachLinkModule>
        <SplitkitModule
          conditionalRegisteredExperiments={conditionalRegisteredExperiments}
        >
          <BrowserBarOpenModule>
            <BottomBarEnabledModule>
              <OmViewModule>
                <OmViewAnimation>{children}</OmViewAnimation>
              </OmViewModule>
            </BottomBarEnabledModule>
          </BrowserBarOpenModule>
        </SplitkitModule>
      </ReachLinkModule>
    </ThemingModule>
  );
};

export type LoggedOutHubProps = StaticHubProps;

export const LoggedOutHub: React.FC<LoggedOutHubProps> = ({ children }) => {
  return (
    <ViewerDataModule loginState="LOGGED_OUT">
      <StaticHub>
        <ActivityCenterModule>{children}</ActivityCenterModule>
      </StaticHub>
    </ViewerDataModule>
  );
};

export type LoggedInState = Exclude<LoginState, 'LOGGED_OUT'>;

export type LoggedInHubProps = StaticHubProps & {
  loginState: LoggedInState;
};

export const LoggedInHub: React.FC<LoggedInHubProps> = ({
  children,
  loginState,
}) => {
  const { loading, data } = useQuery(FeatureSwitchesQueryDocument, {
    errorPolicy: 'all',
  });
  const { __typename, ...features } = data?.viewer?.features ?? {};
  const featureSwitches = data?.viewer?.featureSwitches ?? undefined;

  return (
    <ViewerDataModule loginState={loginState}>
      <FeatureSwitchesModule
        features={asyncResult(loading ? undefined : features)}
        featureSwitches={asyncResult(featureSwitches)}
      >
        <NotificationsModule pollInterval={60_000}>
          <StaticHub>{children}</StaticHub>
        </NotificationsModule>
      </FeatureSwitchesModule>
    </ViewerDataModule>
  );
};
